import { useCallback, useEffect, useState } from 'react';
import type { TourStep } from '../constants';
import { getDefaultStorage, hasCompletedTour, markTourCompleted } from '../utils/tourStorage';

export interface FirstAccessTour {
  isOpen: boolean;
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  back: () => void;
  finish: () => void;
}

/**
 * Controls the first-access tour: opens it once per user and records completion.
 * @param userId - Stable user identifier (OIDC `sub`); the tour stays closed while undefined.
 * @param steps - Ordered tour steps (must not be empty).
 * @param storage - Storage backend used to persist completion; `null` keeps the tour closed.
 * @returns Tour state and navigation actions.
 */
export function useFirstAccessTour(
  userId: string | undefined,
  steps: TourStep[],
  storage: Storage | null = getDefaultStorage(),
): FirstAccessTour {
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!userId) return;
    setStepIndex(0);
    setIsOpen(!hasCompletedTour(storage, userId));
  }, [userId, storage]);

  const finish = useCallback(() => {
    if (userId) markTourCompleted(storage, userId);
    setIsOpen(false);
  }, [userId, storage]);

  const isLast = stepIndex === steps.length - 1;

  const next = useCallback(() => {
    if (isLast) {
      finish();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [isLast, finish]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  return {
    isOpen,
    step: steps[stepIndex],
    stepIndex,
    totalSteps: steps.length,
    isFirst: stepIndex === 0,
    isLast,
    next,
    back,
    finish,
  };
}
